import React, { useState, useEffect } from 'react';
import { Typography, Button, Table, message, Input, Modal, Progress, Space, Tooltip } from 'antd';
import { ExportOutlined, DownloadOutlined } from '@ant-design/icons';
import { getTemplate } from '../utils/printerUtils';
import html2canvas from 'html2canvas';
import { fetchBatches, exportBatch, fetchBatchOrders } from '../services/batchService';
import OrderList from './orders/OrderList';
import OrderDetailModal from './orders/OrderDetailModal';
import './MyBatches.css';

const { Text } = Typography;
const { Search } = Input;

const DEFAULT_ORDER_PAGE_SIZE = 500;

const MyBatches = () => {
  const [loading, setLoading] = useState(true);
  const [allBatches, setAllBatches] = useState([]);
  const [filteredBatches, setFilteredBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [orders, setOrders] = useState([]);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderPagination, setOrderPagination] = useState({
    current: 1,
    pageSize: DEFAULT_ORDER_PAGE_SIZE,
    total: 0
  });
  const [searchText, setSearchText] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [inspectionCount, setInspectionCount] = useState(0);
  const [showInspectionOnly, setShowInspectionOnly] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [batchToDownload, setBatchToDownload] = useState(null);

  const fetchBatchData = async () => {
    try {
      setLoading(true);
      const response = await fetchBatches();
      setAllBatches(response.results);
      setFilteredBatches(response.results);
    } catch (error) {
      console.error('Error fetching batches:', error);
      message.error('加载批次数据失败');
    } finally {
      setLoading(false);
    }
  };

  const loadBatchOrders = async (batchId, page = 1) => {
    if (!batchId) return;
    
    try {
      setOrderLoading(true);
      const response = await fetchBatchOrders(batchId, page, DEFAULT_ORDER_PAGE_SIZE);
      setOrders(response.results);
      setOrderPagination({
        ...orderPagination,
        current: page,
        total: response.count
      });
      // Count inspection orders
      const inspectionOrders = response.results.filter(order => 
        order.status === 'INSPECTION_NEEDED' || order.status === 'INSPECTED'
      );
      setInspectionCount(inspectionOrders.length);
    } catch (error) {
      console.error('Error fetching batch orders:', error);
      message.error('加载订单失败');
      setOrders([]);
      setInspectionCount(0);
    } finally {
      setOrderLoading(false);
    }
  };

  useEffect(() => {
    fetchBatchData();
  }, []);

  useEffect(() => {
    if (selectedBatch) {
      loadBatchOrders(selectedBatch.id, 1);
      setIsSearchMode(false);
      setShowInspectionOnly(false);
    } else {
      setOrders([]);
      setInspectionCount(0);
    }
  }, [selectedBatch]);

  const handleSearch = (value) => {
    setSearchText(value);
    if (!value) {
      setFilteredBatches(allBatches);
    } else {
      const filtered = allBatches.filter(batch => 
        batch.batch_number.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredBatches(filtered);
    }
  };

  const handleOrderPageChange = (page) => {
    if (selectedBatch) {
      loadBatchOrders(selectedBatch.id, page);
    }
  };

  const generateLabel = async (order) => {
    try {
      const tempDiv = document.createElement('div');
      tempDiv.style.width = '100mm';
      tempDiv.style.height = '150mm';
      tempDiv.style.padding = '0';
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.backgroundColor = 'white';
      tempDiv.style.boxSizing = 'border-box';

      let template;
      try {
        template = getTemplate(order.courier_service, order.port);
      } catch (error) {
        console.error('Template selection failed:', error);
        message.error(error.message);
        return null;
      }

      const orderWithCorrectAddress = {
        ...order,
        recipient_name: order.label_address ? order.label_recipient : order.recipient_name,
        recipient_phone: order.label_address ? order.label_phone : order.recipient_phone,
        recipient_address: order.label_address ? order.label_address : order.recipient_address
      };
      const labelHtml = await template(orderWithCorrectAddress);
      if (!labelHtml) {
        message.error('面单生成失败');
        return null;
      }

      tempDiv.innerHTML = labelHtml;
      document.body.appendChild(tempDiv);

      await new Promise(resolve => setTimeout(resolve, 300));

      const canvas = await html2canvas(tempDiv, {
        scale: 4,
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 378,
        height: 567,
        onclone: (clonedDoc) => {
          const clonedDiv = clonedDoc.querySelector('div');
          if (clonedDiv) {
            clonedDiv.style.width = '378px';
            clonedDiv.style.height = '567px';
          }
        }
      });

      document.body.removeChild(tempDiv);
      return canvas;
    } catch (error) {
      console.error('Label generation error:', error);
      message.error('面单生成失败');
      return null;
    }
  };

  const handleDownloadLabels = async (batch, e) => {
    e.stopPropagation();
    setBatchToDownload(batch);
    setDownloadModalVisible(true);
  };

  const handleConfirmDownload = async () => {
    setIsGenerating(true);
    setDownloadProgress(0);

    try {
      // Get all orders for the batch
      const response = await fetchBatchOrders(batchToDownload.id, 1, batchToDownload.order_count);
      const orders = response.results;
      const totalOrders = orders.length;
      const labelCanvases = [];

      // Generate labels with progress updates
      for (let i = 0; i < orders.length; i++) {
        const canvas = await generateLabel(orders[i]);
        if (canvas) {
          labelCanvases.push({
            canvas,
            orderNumber: orders[i].order_number
          });
        }
        setDownloadProgress(Math.round(((i + 1) / totalOrders) * 100));
      }

      if (labelCanvases.length === 0) {
        throw new Error('No labels were generated successfully');
      }

      // Convert canvases to PDFs and create zip
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      // Add each label as a JPEG to the zip
      labelCanvases.forEach(({ canvas, orderNumber }) => {
        const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
        const base64Data = dataUrl.split(',')[1];
        zip.file(`${orderNumber}.jpg`, base64Data, { base64: true });
      });

      // Generate and download zip
      const content = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.download = `批次${batchToDownload.batch_number}_面单.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('面单下载成功');
    } catch (error) {
      console.error('Download error:', error);
      message.error('面单下载失败');
    } finally {
      setIsGenerating(false);
      setDownloadModalVisible(false);
      setDownloadProgress(0);
      setBatchToDownload(null);
    }
  };

  const handleExportBatch = async (batch, e) => {
    e.stopPropagation();
    try {
      await exportBatch(batch);
      message.success('批次导出成功');
    } catch (error) {
      console.error('Error exporting batch:', error);
      message.error('导出批次失败');
    }
  };

  const handleSearchResults = (results) => {
    setOrders(results);
    setIsSearchMode(true);
  };

  const handleClearSearch = () => {
    setIsSearchMode(false);
    setShowInspectionOnly(false);
    if (selectedBatch) {
      loadBatchOrders(selectedBatch.id, 1);
    }
  };

  const toggleInspectionFilter = () => {
    setShowInspectionOnly(!showInspectionOnly);
    if (!showInspectionOnly) {
      const inspectionOrders = orders.filter(order => 
        order.status === 'INSPECTION_NEEDED' || order.status === 'INSPECTED'
      );
      handleSearchResults(inspectionOrders);
    } else {
      handleClearSearch();
    }
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setModalVisible(true);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  const columns = [
    {
      title: '批次号',
      dataIndex: 'batch_number',
      key: 'batch_number',
      ellipsis: true,
      width: 60,
    },
    {
      title: '数量',
      dataIndex: 'order_count',
      key: 'order_count',
      width: 40,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 60,
      render: (text) => formatDate(text),
    },
    {
      title: '',
      key: 'action',
      width: 50,
      align: 'left',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="导出批次">
            <Button
              type="text"
              size="small"
              icon={<ExportOutlined />}
              onClick={(e) => handleExportBatch(record, e)}
              style={{ color: '#666', padding: '0 4px', height: '16px', fontSize: '11px' }}
            />
          </Tooltip>
          <Tooltip title="下载面单">
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined />}
              onClick={(e) => handleDownloadLabels(record, e)}
              style={{ color: '#666', padding: '0 4px', height: '16px', fontSize: '11px' }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="my-batches-container" style={{ padding: '8px' }}>
      <div style={{ display: 'flex', gap: '8px' }}>
        <div style={{ width: '25%', minWidth: '200px' }}>
          <Search
            placeholder="搜索批次号"
            allowClear
            value={searchText}
            onChange={e => handleSearch(e.target.value)}
            style={{ width: '100%', marginBottom: '4px' }}
            size="small"
          />
          <Table
            columns={columns}
            dataSource={filteredBatches}
            rowKey="id"
            pagination={false}
            loading={loading}
            size="small"
            onRow={(record) => ({
              onClick: () => setSelectedBatch(record),
              style: { 
                cursor: 'pointer',
                backgroundColor: selectedBatch?.id === record.id ? '#f0f0f0' : undefined,
                fontSize: '11px',
                padding: 0
              }
            })}
            style={{ 
              '.ant-table': { fontSize: '11px' },
              '.ant-table-thead > tr > th': { 
                padding: '2px 4px', 
                fontSize: '11px',
                background: '#fafafa',
                borderBottom: '1px solid #f0f0f0'
              },
              '.ant-table-tbody > tr > td': { 
                padding: '1px 4px',
                borderBottom: '1px solid #f0f0f0'
              }
            }}
          />
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {selectedBatch ? (
            <>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                padding: '4px 8px',
                background: '#fafafa',
                borderBottom: '1px solid #f0f0f0',
                marginBottom: '4px'
              }}>
                <Text style={{ fontSize: '13px' }}>订单总数：{orderPagination.total}</Text>
                <Button
                  type={showInspectionOnly ? "primary" : "default"}
                  size="small"
                  onClick={toggleInspectionFilter}
                  style={{ fontSize: '13px', height: '24px', padding: '0 8px' }}
                >
                  查验件：{inspectionCount}
                </Button>
              </div>
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <OrderList
                  orders={orders}
                  loading={orderLoading}
                  pagination={orderPagination}
                  onPageChange={handleOrderPageChange}
                  showBatchButtons={false}
                  isSearchMode={isSearchMode}
                  onSearchResults={handleSearchResults}
                  onClearSearch={handleClearSearch}
                  onOrdersUpdate={() => {
                    if (selectedBatch) {
                      loadBatchOrders(selectedBatch.id, 1);
                    }
                  }}
                  onOrderClick={handleOrderClick}
                />
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', color: '#999', padding: '16px 0', fontSize: '11px' }}>
              <Text>请选择左侧批次查看订单</Text>
            </div>
          )}
        </div>
      </div>
    </div>

    <OrderDetailModal
      visible={modalVisible}
      order={selectedOrder}
      onClose={() => setModalVisible(false)}
    />

    <Modal
      title="下载面单"
      open={downloadModalVisible}
      onOk={handleConfirmDownload}
      onCancel={() => {
        setDownloadModalVisible(false);
        setBatchToDownload(null);
      }}
      okText="确认"
      cancelText="取消"
      okButtonProps={{ disabled: isGenerating }}
      cancelButtonProps={{ disabled: isGenerating }}
    >
      {isGenerating ? (
        <div>
          <p>正在生成面单，请稍候...</p>
          <Progress percent={downloadProgress} status="active" />
        </div>
      ) : (
        <p>确定下载此批次的所有面单PDF文件吗？</p>
      )}
    </Modal>

    </>
  );
};

export default MyBatches;
